import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, BadgeCheck, FileText } from "lucide-react";
import { motion } from "framer-motion";
import {
  FaShieldAlt, // For Zero Depreciation
  FaTools, // For Engine Protection
  FaFileInvoiceDollar, // For RTI
  FaTruckPickup, // For Roadside Assistance
  FaCarSide, // For Tyre and Rim Cover
  FaCloudSunRain,
  FaUserLock,
  FaCarCrash,
} from "react-icons/fa";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${
        id === open ? "rotate-180" : ""
      } h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const KiaCarInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "Is zero depreciation worth it for Kia Seltos or Carens?",
      answer:
        "Yes, especially in the first few years where parts cost more to replace.",
    },
    {
      question: "Can I insure a Kia EV like EV6 or QYi?",
      answer:
        "Yes, newer policies offer EV-specific coverage including battery protection.",
    },
    {
      question: "Which Kia model has the lowest insurance premium?",
      answer:
        "Sonet generally has the lowest premiums among Kia models due to its lower IDV and engine size.",
    },
    {
      question: "Is it possible to renew expired Kia insurance online?",
      answer:
        "Yes, most insurers allow online renewal, though inspection may be needed for policies lapsed significantly.",
    },
    {
      question: "Are EV Kia premiums higher than petrol models?",
      answer: "Usually yes, due to higher IDV and EV-specific components.",
    },
    {
      question: "What happens to insurance if I sell my Kia car?",
      answer:
        "You can transfer the insurance to the new owner or cancel the policy and claim a refund.",
    },
    {
      question: "Are modifications like alloy wheels or body kits covered?",
      answer:
        "Only if declared and added to the insurance policy, otherwise claims may be rejected.",
    },
    {
      question: "Does comprehensive Kia policy cover natural disasters?",
      answer:
        "Yes, comprehensive plans cover floods, storms, fire, theft and vandalism.",
    },
    {
      question: "Can I transfer NCB from another car to my Kia?",
      answer:
        "Yes, NCB is transferable as long as the new car is registered in your name.",
    },
    {
      question: "What is RTI and is it good for Kia models?",
      answer:
        "Return to Invoice cover ensures restoration of full invoice value in case of total loss or theft.",
    },
    {
      question: "Are alloy wheels and tyres covered under basic policies?",
      answer:
        "Only for accident-related damage. You can add tyre cover for full protection.",
    },
    {
      question: "Do insurers offer cashless garages for Kia vehicles?",
      answer:
        "Yes, many insurers have tie-ups with Kia-authorized service centers across the country.",
    },
    {
      question: "Can I add family members as named drivers?",
      answer:
        "Yes, most insurers allow multiple drivers under the same policy.",
    },
    {
      question: "How long does claim settlement take for Kia insurance?",
      answer:
        "Typically 7 to 14 working days depending on documentation and verification.",
    },
    {
      question: "Does car colour affect Kia insurance premium?",
      answer: "No, the colour does not influence the premium.",
    },
    {
      question: "Can I switch insurers mid-policy?",
      answer:
        "Generally no. Switching is possible only at renewal unless exceptions apply.",
    },
    {
      question: "What is the penalty for driving a Kia without insurance?",
      answer:
        "Penalties range from ₹2,000 to ₹4,000 and could include licence suspension.",
    },
    {
      question: "Does Kia offer its own insurance options?",
      answer:
        "Some Kia dealerships tie up with partner insurers but independent comparison is recommended.",
    },
    {
      question: "Is engine protection add-on necessary for Kia SUVs?",
      answer:
        "It is valuable especially in cities with poor drainage or during monsoon season.",
    },
    {
      question: "Can used Kia cars be insured comprehensively?",
      answer:
        "Yes, most insurers cover used Kia vehicles after inspection, depending on age and condition.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Zero Depreciation",
      description:
        "Highly useful for Seltos, Carens or EV6 models with costly plastic/plastic parts.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Engine and Gearbox Protection",
      description:
        "Valuable for EV users or hybrid models in flood-prone zones.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "Return to Invoice (RTI)",
      description:
        "Especially beneficial for newer Sonet, Seltos or Carens owners.",
      icon: <FaFileInvoiceDollar className="text-4xl text-orange-600 mb-4" />,
    },
    {
      title: "Roadside Assistance",
      description: "Handy for highway users or long-distance travel.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Tyre and Rim Cover",
      description: "Recommended for alloy wheel variants or EV models.",
      icon: <FaCarSide className="text-4xl text-green-600 mb-4" />,
    },
  ];

  const features = [
    { icon: "🛡️", text: "Reliable & Accurate Estimates" },
    { icon: "⏱️", text: "Takes Less Than a Minute" },
    { icon: "📉", text: "Instantly Shows Premium Breakup" },
    { icon: "📄", text: "No Paperwork or Documentation" },
    { icon: "📲", text: "Easy to Use on Any Device" },
    { icon: "💰", text: "Helps You Save on Insurance" },
  ];
const kiainsurance = [
    {
      name: 'Seltos',
      path: '/kia-seltos-car-insurance',
      image: '/assets/kiaseltos.jpeg',
    },
      {
      name: 'Sonet',
      path: '/kia-sonet-car-insurance',
      image: '/assets/kiasonet.jpeg',
    },
      {
      name: 'Carens',
      path: '/kia-carens-car-insurance',
      image: '/assets/kiacarens.jpeg',
    },
       {
      name: 'EV6',
      path: '/kia-ev6-car-insurance',
      image: '/assets/kiaev6.jpeg',
    },
       {
      name: 'Carnival',
      path: '/kia-carnival-car-insurance',
      image: '/assets/kiacarnival.jpeg',
    },
     {
      name: 'Sportage',
      path: '/kia-sportage-car-insurance',
      image: '/assets/kiasportage.jpeg',
    },
     {
      name: 'EV9',
      path: '/kia-ev9-car-insurance',
      image: '/assets/kiaev9.jpeg',
    },
      {
      name: 'Rio',
      path: '/kia-rio-car-insurance',
      image: '/assets/kiario.jpeg',
    },
  ];
  return (
    <>
      <Helmet>
        <title>Kia Car Insurance in India – Compare Quotes & Buy Online</title>
        <meta
          name="description"
          content="Explore Kia car insurance options in India. Compare quotes, customize coverage, and buy online to protect your Kia vehicle."
        />
        <meta
          name="keywords"
          content="kia car insurance, auto insurance india, kia seltos insurance, kia sonet insurance, car insurance quotes"
        />
          <link rel="canonical" href="https://thefinmaster.com/kia-car-insurance" />
       <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
       
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">Kia Car Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">
                    Kia Motors Car Insurance
                  </span>{" "}
                 
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                Kia Motors has quickly made its mark in India with stylish designs, advanced features, and reliable performance. Whether you own a compact Sonet or a premium Carnival, safeguarding your car begins with choosing the right insurance. This guide walks you through everything you need to know about Kia Motors insurance, from average premiums and model insights to useful add-ons, renewal tips, and frequently asked questions.

                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/kiainsurance.jpeg"
                alt="Kia Car"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center p-4 mt-6">
        <div className="bg-white shadow-lg rounded-2xl p-6 max-w-7xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            About Kia Motors in India
          </h2>
          <p className="text-gray-600 leading-relaxed">
            Kia Motors India launched with models that tapped into aspirational
            buyers looking for modern features and reliability. The brand is
            backed by strong build quality, innovative styling and growing
            acceptance in urban markets. With popular models like Seltos, Sonet,
            Carens and EV options like EV6, Kia appeals to a diverse audience.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            If you own a Kia vehicle, combining it with a well-structured
            insurance plan can offer both legal compliance and long-term
            financial protection.
          </p>
        </div>
      </div>


       <div className="p-4 sm:p-8 bg-white">
                              <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
                                Choose Your Car Brand
                              </h2>
                              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                          {kiainsurance.map((calc, index) => (
                            <motion.div
                              key={index}
                              whileHover={{ scale: 1.04 }}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.3, delay: index * 0.05 }}
                            >
                              <Link
                                to={calc.path}
                                className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
                              >
                                <img
                                  src={calc.image}
                                  alt={calc.name}
                                  className="w-16 h-16 object-contain mb-2"
                                />
                                <span className="text-md font-medium text-gray-800 break-words leading-snug">
                                  {calc.name}
                                </span>
                              </Link>
                            </motion.div>
                          ))}
                        </div>
                        </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 opacity-20 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-400 opacity-20 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Why Kia Owners Need a Thoughtful Insurance Plan
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            Kia vehicles often come with advanced infotainment systems, premium
            interiors, turbocharged powertrains and hybrid or EV options.
            Repairing or replacing certain parts can be expensive. A
            comprehensive policy ensures coverage for:
          </p>
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-gray-700 mb-6">
            <li className="flex items-start gap-3">
              <FaCarCrash className="text-xl text-red-500 mt-1" />
              <span>Road accidents and collision damage</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserLock className="text-xl text-yellow-500 mt-1" />
              <span>Theft and vandalism</span>
            </li>
            <li className="flex items-start gap-3">
              <FaCloudSunRain className="text-xl text-blue-500 mt-1" />
              <span>
                Damage from natural events such as floods, storms or landslides
              </span>
            </li>
            <li className="flex items-start gap-3">
              <FaShieldAlt className="text-xl text-green-500 mt-1" />
              <span>Third-party injury or property damage liabilities</span>
            </li>
            <li className="flex items-start gap-3">
              <FaTools className="text-xl text-indigo-500 mt-1" />
              <span>
                Rising repair costs due to technology-laden components
              </span>
            </li>
          </ul>
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-700">
            Insurance also grants access to cashless repairs at Kia-tagged
            service centers, faster claims and coverage additions tailored to
            your model.
          </div>
        </motion.div>
      </section>
      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="max-w-xl space-y-6 text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
              Calculate Your Car Insurance <br />
              in <span className="text-blue-600">Seconds</span>
            </h2>
            <p className="text-gray-700 text-lg">
              Use our fast and free calculator to estimate your premium — no
              paperwork, no waiting.
            </p>
            <Link
              to="/financial-tools-calculators/car-insurance-calculator"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
            >
              Start Calculating <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="space-y-4 text-left">
            {features.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xl" role="img" aria-label={item.text}>
                  {item.icon}
                </span>
                <span className="text-gray-800 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Popular Kia Models and Insurance Cost Estimates
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Here is a snapshot of Kia’s most popular cars in India, along with
          estimated IDV range and starting annual premiums for comprehensive
          cover:
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              {
                model: "Sonet",
                category: "Subcompact SUV",
                idv: "7–10 Lakh",
                premium: "₹3,300 to ₹5,500",
              },
              {
                model: "Seltos",
                category: "Compact SUV",
                idv: "10–16 Lakh",
                premium: "₹4,200 to ₹7,800",
              },
              {
                model: "Carens",
                category: "MPV / SUV",
                idv: "12–18 Lakh",
                premium: "₹4,500 to ₹8,200",
              },
              {
                model: "EV6 (EV)",
                category: "Electric SUV",
                idv: "34–41 Lakh",
                premium: "₹7,500 to ₹13,000",
              },
              {
                model: "Carnival",
                category: "MPV",
                idv: "22–28 Lakh",
                premium: "₹6,000 to ₹9,500",
              },
              {
                model: "QYi (Mid-size EV)",
                category: "Electric Sedan",
                idv: "25–32 Lakh",
                premium: "₹7,000 to ₹11,000",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="border mb-4 p-4 rounded-lg bg-white shadow-md"
              >
                <h3 className="font-semibold text-gray-800 mb-2">
                  {item.model}
                </h3>
                <div className="text-gray-600 mb-1">
                  <strong>Category:</strong> {item.category}
                </div>
                <div className="text-gray-600 mb-1">
                  <strong>Approx. IDV:</strong> {item.idv}
                </div>
                <div className="text-gray-600">
                  <strong>Starting Premium:</strong> {item.premium}
                </div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">
                    Model
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Category
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Approx. IDV (₹)
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Starting Premium (₹)
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  ["Sonet", "Subcompact SUV", "7–10 Lakh", "₹3,300 to ₹5,500"],
                  ["Seltos", "Compact SUV", "10–16 Lakh", "₹4,200 to ₹7,800"],
                  ["Carens", "MPV / SUV", "12–18 Lakh", "₹4,500 to ₹8,200"],
                  [
                    "EV6 (EV)",
                    "Electric SUV",
                    "34–41 Lakh",
                    "₹7,500 to ₹13,000",
                  ],
                  ["Carnival", "MPV", "22–28 Lakh", "₹6,000 to ₹9,500"],
                  [
                    "QYi (Mid-size EV)",
                    "Electric Sedan",
                    "25–32 Lakh",
                    "₹7,000 to ₹11,000",
                  ],
                ].map(([model, category, idv, premium], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{category}</td>
                    <td className="p-3 border border-gray-300">{idv}</td>
                    <td className="p-3 border border-gray-300">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Add-Ons That Make Sense for Kia Cars
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Kia models come with premium trims, alloy wheels, tech-loaded
          dashboards and hybrid/EV variants. The following add-ons are worth
          considering:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="mx-auto container p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Best Insurance Companies for Kia Cars
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          These insurers are commonly chosen by Kia owners for their claim
          servicing, add-on flexibility and network garages:
        </p>
        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🏥</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              HDFC ERGO
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Known for fast digital claims and wide garage tie-ups.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              ICICI Lombard
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Strong options for EV and premium Kia models.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Reliance General
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Affordable quotes and easy renewal process.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📋</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Tata AIG
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Good for bundled cover options and brand-aligned clients.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💰</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              SBI General
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Simplified online policies, ideal for smaller Kia trims.
            </p>
          </div>
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                How to Save on Kia Car Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              Here are some practical ways to reduce premium costs without
              sacrificing coverage:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Compare quotes before buying or renewing your policy.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Maintain your No Claim Bonus (NCB) by avoiding small claims.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Choose voluntary deductibles if you drive carefully.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Install ARAI-approved anti-theft devices for discounts.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Renew your policy before expiry to avoid penalties and
                  preserve NCB.
                </span>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-emerald-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required for Kia Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              To buy or renew your Kia vehicle’s insurance, you generally need:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Vehicle Registration Certificate (RC)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Previous car insurance policy (if applicable)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Pollution Under Control (PUC) certificate</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Identity proof such as PAN or Aadhaar</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl sm:col-span-2">
                <span>
                  Passport-size photographs (if required for offline processing)
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          Kia Car Insurance FAQs
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <FaCarSide />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Kia vehicles combine style, technology and modern design with
                driving comfort and safety. A tailored insurance plan with
                relevant add-ons ensures you can enjoy every journey with
                confidence. Whether you drive a Sonet or EV6, comprehensive
                coverage can save you from unexpected financial burden.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                At TheFinMaster, we help you compare plans, choose the ideal
                add-ons and pick the best insurance plans tailored to your Kia
                vehicle.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default KiaCarInsurance;
